import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { canManageChallenge } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { IdeasLibUser } from "../types";
import { mapOwnerImpacts } from "./mapOwnerImpacts";
import { impactStageDefinitions } from "./statementUtils";

type ImpactStatements =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"];
type ImpactStatementStage = OpenAPI.Schemas["ImpactStatement"]["stage"];
type ImpactStatement = NonNullable<ImpactStatements[ImpactStatementStage]>;
type ImpactStatementImpact = NonNullable<ImpactStatement["impacts"]>[string];

export const useImpactStatements = (ideaId: string | undefined) => {
  const { t } = useTranslation();
  const user: IdeasLibUser = useAppSelector((state) => state.user);
  const [creatingStage, setCreatingStage] =
    useState<ImpactStatementStage | null>(null);

  const impactStatementQuery = useQuery(
    initUseQuery(api.ideas.getImpactStatements, [ideaId!], {
      isEnabled: !!ideaId,
    }),
  );

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], { isEnabled: !!ideaId }),
  );

  const idea = ideaQuery.data;
  const impactStatements = impactStatementQuery.data?.impactStatements ?? {};
  const statementDefinition = impactStatementQuery.data?.statementDefinition;
  const mappedImpactCategories = impactStatementQuery.data
    ? mapOwnerImpacts(impactStatementQuery.data)
    : [];

  const userIsAssigned = (() => {
    if (!user?._id) return false;
    const boards = idea?.projectManagement?.boards || [];
    const pmBoardObject = boards.length
      ? boards.find((b) => b.forId === idea?.ownerChallenge?._id)
      : null;
    return !!(pmBoardObject?.assignees || []).find(
      (assigneeId) => assigneeId === user._id,
    );
  })();

  const userIsAssessor =
    !!user?._id &&
    !!(idea?.assessments || []).find(
      (assessment) => assessment?.user === user._id,
    );

  const canManageIdea = useMemo(
    () =>
      !!idea &&
      (canManageChallenge(user, idea.ownerChallenge) || userIsAssigned),
    [user, idea, userIsAssigned],
  );

  const canEditProjectStages = useMemo(
    () =>
      userIsAssigned ||
      util.hasPermission(
        user,
        "challenge.editProjectImpacts",
        idea?.challenge,
      ) ||
      (!!idea && util.canManageChallenge(user, idea.ownerChallenge)),
    [user, idea, userIsAssigned],
  );

  const restrictStagesTo = useMemo((): ImpactStatementStage[] | undefined => {
    if (canManageIdea) return undefined;
    if (userIsAssessor) {
      return (statementDefinition?.assessorVisibility || [
        "userEstimate",
      ]) as ImpactStatementStage[];
    }
    return ["userEstimate"];
  }, [canManageIdea, userIsAssessor, statementDefinition?.assessorVisibility]);

  const updateQueryImpactStatements = useCallback(
    (updater: (prev: ImpactStatements) => ImpactStatements) => {
      if (!ideaId) return;
      api.setQueryData(api.ideas.getImpactStatements, [ideaId], (prev) => ({
        ...prev,
        impactStatements: updater(prev?.impactStatements || {}),
      }));
    },
    [ideaId],
  );

  const updateIdeaCompletedStages = useCallback(
    (stage: ImpactStatementStage, completed: boolean) => {
      if (!idea) return;
      util.onUpdateIdea(idea, {
        completedImpactStages: {
          ...(idea.completedImpactStages || {}),
          [stage]: completed,
        },
      });
    },
    [idea],
  );

  const invalidateRelatedQueries = useCallback(() => {
    if (!ideaId) return;
    api.invalidateQueries(api.ideas.get, [ideaId]);
    if (idea?.challenge) {
      api.invalidateQueries(api.challenges.getIdeaImpacts, [idea.challenge]);
    }
  }, [ideaId, idea]);

  const createImpactStatement = useCallback(
    (forStage: ImpactStatementStage, onSettled?: () => void) => {
      if (!ideaId) return;

      const previousStage = impactStageDefinitions[forStage]?.previousStage;
      const previousStatement = previousStage
        ? impactStatementQuery.data?.impactStatements?.[previousStage]
        : undefined;
      const impacts = previousStatement?.impacts ?? {};
      const description = previousStatement?.description ?? "";

      setCreatingStage(forStage);
      api.ideas.createImpactStatement(
        ideaId,
        { impacts, description, stage: forStage },
        ({ impactStatement: newImpactStatement }) => {
          updateQueryImpactStatements((prev) => ({
            ...prev,
            [forStage]: newImpactStatement,
          }));
          if (forStage !== "userEstimate") {
            updateIdeaCompletedStages(forStage, true);
          }
          invalidateRelatedQueries();
          setCreatingStage(null);
          onSettled?.();
        },
        () => {
          toast.error(
            `Failed to save ${t("generic.impact")} statement. Please try again.`,
          );
          setCreatingStage(null);
          onSettled?.();
        },
      );
    },
    [
      ideaId,
      impactStatementQuery.data?.impactStatements,
      t,
      updateQueryImpactStatements,
      updateIdeaCompletedStages,
      invalidateRelatedQueries,
    ],
  );

  const saveImpactStatement = useThrottle(
    (newStatement: ImpactStatement, stage: ImpactStatementStage) => {
      if (!ideaId || !newStatement._id) return;
      api.ideas.updateImpactStatement(
        ideaId,
        newStatement._id,
        {
          impacts: newStatement.impacts,
          description: newStatement.description,
        },
        ({ impactStatement }) => {
          updateQueryImpactStatements((prev) => ({
            ...prev,
            [stage]: impactStatement,
          }));
          invalidateRelatedQueries();
        },
        () => {
          toast.error(
            `Failed to update ${t("generic.impact")} statement. Please try again.`,
          );
        },
      );
    },
    400,
    [ideaId, t, updateQueryImpactStatements, invalidateRelatedQueries],
  );

  const patchImpactStatement = useCallback(
    (
      stage: ImpactStatementStage,
      updater: (statement: ImpactStatement) => ImpactStatement,
    ) => {
      if (!ideaId) return;
      let nextStatement: ImpactStatement | undefined;
      updateQueryImpactStatements((prev) => {
        const current = prev[stage];
        if (!current) return prev;
        nextStatement = updater(current);
        return { ...prev, [stage]: nextStatement };
      });
      if (nextStatement?._id) {
        saveImpactStatement(nextStatement, stage);
      }
    },
    [ideaId, updateQueryImpactStatements, saveImpactStatement],
  );

  const patchStatementImpact = useCallback(
    (
      stage: ImpactStatementStage,
      impactId: string,
      updater: (prev: ImpactStatementImpact) => ImpactStatementImpact,
    ) => {
      patchImpactStatement(stage, (statement) => {
        const currentImpact = statement.impacts?.[impactId] ?? {};
        return {
          ...statement,
          impacts: {
            ...statement.impacts,
            [impactId]: { ...currentImpact, ...updater(currentImpact) },
          },
        };
      });
    },
    [patchImpactStatement],
  );

  const deleteImpactStatement = useCallback(
    (
      estimateType: Exclude<ImpactStatementStage, "userEstimate">,
      impactStatementId: string,
    ) => {
      if (!ideaId || !idea) return;
      api.ideas.deleteImpactStatement(
        ideaId,
        impactStatementId,
        () => {
          toast.success("Statement deleted successfully");
          updateQueryImpactStatements((prev) => ({
            ...prev,
            [estimateType]: undefined,
          }));
          updateIdeaCompletedStages(estimateType, false);
          api.invalidateQueries(api.challenges.getIdeas, [idea.challenge]);
          invalidateRelatedQueries();
        },
        () => {
          toast.error("Failed to delete statement");
        },
      );
    },
    [
      ideaId,
      idea,
      updateQueryImpactStatements,
      updateIdeaCompletedStages,
      invalidateRelatedQueries,
    ],
  );

  return {
    impactStatements,
    statementDefinition,
    mappedImpactCategories,
    isLoading: impactStatementQuery.isLoading,
    canManageIdea,
    canEditProjectStages,
    userIsAssigned,
    userIsAssessor,
    restrictStagesTo,
    creatingStage,
    createImpactStatement,
    patchImpactStatement,
    patchStatementImpact,
    deleteImpactStatement,
  };
};
